import { Pets } from "@highmarch/app";
import { priceButtonHtml, secHtml, slotTileHtml } from "../components";
import { fmtCompact } from "../kit";
import { toast } from "../toast";
import { econCue, purchasePop, tickPill } from "../hub/purchaseFx";
import { gemIcon } from "../hub/econArt";
import { openItemPopup } from "../shops/itemPopup";
import { itemCardHtml } from "../shops/itemCard";
import { refusalWords } from "../hub/refusalWords";
import type { ShopDeps } from "../hub/shopSheet";
import { treatArtHtml } from "./treatArt";
import { pct, treatShelfWords } from "./petWords";
import { notEnoughGems } from "../hub/getGold";

/**
 * The Shop's Pet Treats (Pets v2, docs/PETS.md): on the Gems page after the Supplies, the three biscuits as slot tiles
 * like the supplies beside them — the biscuit in a navy well with how many are held, the coat chance it gives a wild
 * pup, and its gem-price button. A buy confirms, spends once through @highmarch/app Pets.buyTreat (hub only), then ticks the
 * gem pill and says where it waits (the battle card's treat well). Mounted by shopSheet.ts.
 */
export function treatShelfHtml(pets: Pets, gems: number): string {
  const w = treatShelfWords();
  const tiles = pets.treats().map(({ treat, have, skinChancePercent }) => {
    const price = priceButtonHtml({ currency: "gems", amount: fmtCompact(treat.gems), short: gems < treat.gems, className: "ebuy b-treat", data: `data-treat="${treat.id}"` });
    return slotTileHtml({
      well: { art: treatArtHtml(treat.id, 56), count: have > 1 ? `×${have}` : "" }, name: treat.name, left: w.chance(pct(skinChancePercent)), price,
      className: "pack treat", data: `data-treat="${treat.id}"`,
    });
  }).join("");
  // owner 2026-10-24: no help line under the title (the tile's popup says what a treat does)
  return `<section class="ssec packs treats">${secHtml(w.title, "", treatArtHtml("biscuit", 20))}<div class="k-grid3 pgrid supgrid">${tiles}</div></section>`;
}

/** Draws the shelf at the end of `container` (the Gems page's goods) and binds its buttons; redraws in place after a buy. */
export function mountPetTreatsShelf(container: HTMLElement, d: ShopDeps): void {
  const pets = new Pets(d.catalog), wallet = d.catalog.economy.wallet, w = treatShelfWords();
  const slot = document.createElement("div");
  slot.className = "treatslot";
  container.append(slot);
  const draw = (): void => {
    slot.innerHTML = treatShelfHtml(pets, wallet.gems);
    // the whole tile opens the item popup (its price button's tap bubbles to the tile)
    for (const t of slot.querySelectorAll<HTMLElement>(".treat[data-treat]")) t.onclick = () => void buy(t.dataset.treat ?? "", t);
  };
  const buy = async (id: string, card: Element | null): Promise<void> => {
    const slotOf = pets.treats().find((x) => x.treat.id === id);
    if (!slotOf) return;
    const t = slotOf.treat, parent = d.hub.parent;
    const ok = await openItemPopup(parent, { title: w.title.toUpperCase(), price: `${gemIcon(20)} ${fmtCompact(t.gems)}`, className: "c-gems",
      body: itemCardHtml({ well: treatArtHtml(id, 56), name: t.name, rarity: null, what: `${w.chance(pct(slotOf.skinChancePercent))}. ${w.where}` }) });
    if (!ok) return;
    if (wallet.gems < t.gems) { void notEnoughGems(parent, t.gems - wallet.gems, card?.closest(".sheet") ?? null); return; }
    const before = wallet.gems;
    const r = pets.buyTreat(id, d.now(), "hub");
    if (r !== true) { toast(parent, refusalWords(r)); draw(); return; }
    econCue("buy");
    purchasePop(card);
    d.hub.changed();
    tickPill("gems", before, wallet.gems);
    toast(parent, w.bought(t.name));
    draw();
  };
  draw();
}
